import React, { useCallback, useEffect, useRef } from 'react';
import { PublicationFigure } from '../data/publications';
import './FigureLightbox.css';

interface FigureLightboxProps {
  figures: PublicationFigure[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}

// Full-screen gallery viewer for a publication's figures
const FigureLightbox: React.FC<FigureLightboxProps> = ({ figures, index, onIndexChange, onClose }) => {
  const closeRef = useRef<HTMLButtonElement>(null);
  const count = figures.length;
  const figure = figures[index];

  const step = useCallback(
    (delta: number) => onIndexChange((index + delta + count) % count),
    [index, count, onIndexChange]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' && count > 1) step(1);
      else if (e.key === 'ArrowLeft' && count > 1) step(-1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, step, count]);

  // Lock page scroll and move focus into the dialog while it is open,
  // then hand focus back to whatever opened it
  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, []);

  return (
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`Figure ${index + 1} of ${count}`}
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="lightbox-bar">
        <span className="lightbox-counter">{index + 1} / {count}</span>
        <div className="lightbox-bar-actions">
          <a href={figure.src} target="_blank" rel="noopener noreferrer" className="lightbox-bar-btn">
            Open original
          </a>
          <button ref={closeRef} type="button" className="lightbox-bar-btn lightbox-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
      </div>

      <div className="lightbox-stage" onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}>
        {count > 1 && (
          <button type="button" className="lightbox-nav lightbox-prev" onClick={() => step(-1)} aria-label="Previous figure">
            ‹
          </button>
        )}
        <figure className="lightbox-figure">
          <div className="lightbox-image">
            <img src={figure.src} alt={figure.alt} />
          </div>
          {figure.caption && (
            <figcaption>
              <span className="lightbox-figure-num">Figure {index + 1}.</span> {figure.caption}
            </figcaption>
          )}
        </figure>
        {count > 1 && (
          <button type="button" className="lightbox-nav lightbox-next" onClick={() => step(1)} aria-label="Next figure">
            ›
          </button>
        )}
      </div>
    </div>
  );
};

export default FigureLightbox;
